import {
  useLayoutEffect,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import { ProgressBar } from "devextreme-react/progress-bar";
import DataGrid, {
  Column,
  Export,
  HeaderFilter,
  Pager,
  Paging,
  SearchPanel,
} from "devextreme-react/data-grid";
import { DefaultDateFormat, FileType } from "../../../helper/fixcode";
import { useNavigate } from "react-router-dom";
import "./../../../App.css";
import { useSelector } from "react-redux";
import moment from "moment";
import {
  showErrorMessage,
  showResponseMessage,
  showWarningMessage,
} from "../../../helper/message";
import { Loader } from "../../Shared/PageLoader";
import { uploadFileService } from "../../../apis/apiServices/UploadFile";
import * as XLSX from "xlsx";
import { Popup } from "devextreme-react";

const DATE_FORMATS = [
  "DD/MM/YYYY",
  "MM/DD/YYYY",
  "DD-MM-YYYY",
  "MM-DD-YYYY",
  "YYYY-MM-DD",
  "DD MMM YYYY",
  "DD-MMM-YYYY",
];

export default function UploadAssetComponent(props) {
  const user = useSelector((state) => state.user.data);
  const navigate = useNavigate();
  const [state, setState] = useState({
    isCompleteLoading: false,
    isLoading: false,
    loadingMessage: "",
    assetBulkDataSourceLst: [],
    importAssetDataLst: [],
    importAssetCount: 0,
    saveImportedAssetCount: 0,
    processAssetCount: 0,
  });
  const [isOpenPopup, setIsOpenPopup] = useState(false);
  const [remarkArray, setRemarkArray] = useState([]);
  const refDataGrid = useRef(null);
  const refIsImporting = useRef(false);
  const refIsProcessing = useRef(false);

  useLayoutEffect(() => {
    document.querySelector("#pageTitle").innerHTML = `Upload Asset`;
  }, []);

  let isUseEffectRendered = false;
  useEffect(() => {
    const controller = new AbortController();
    try {
      if (isUseEffectRendered) {
        return;
      }
      fetchApiOnPageLoad();
      return () => {
        controller.abort();
        isUseEffectRendered = true;
      };
    } catch (error) {
      console.log(error);
    }
  }, []);

  const fetchApiOnPageLoad = useCallback(async () => {
    try {
      setState((prev) => ({ ...prev, isCompleteLoading: true }));
      await getAssetBulkLst();
      setState((prev) => ({ ...prev, isCompleteLoading: false }));
    } catch (error) {
      console.log(error);
      setState((prevState) => ({
        ...prevState,
        isCompleteLoading: false,
      }));
    }
  }, []);

  const getAssetBulkLst = async () => {
    try {
      const response = await uploadFileService.getAssetBulkDataList(
        Number(FileType.Asset)
      );
      setState((prev) => ({
        ...prev,
        assetBulkDataSourceLst: response?.data ?? [],
      }));
    } catch (error) {
      console.log(error);
    }
  };

  const convertExcelDate = (value, format = "YYYY-MM-DD") => {
    if (value === null || value === undefined || value === "") return "";
    if (value instanceof Date) {
      return moment(value).format(format).toString();
    }
    if (!isNaN(value)) {
      const excelEpoch = new Date(Date.UTC(1899, 11, 30));
      const date = new Date(excelEpoch.getTime() + Number(value) * 86400000);
      return moment(date).format(format).toString();
    }
    const str = value.toString().trim();
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(str)) {
      const [a, b] = str.split("/").map(Number);
      if (a <= 12 && b <= 12) return "";
    }
    const parsed = moment(str, DATE_FORMATS, true);
    return parsed.isValid() ? parsed.format(format).toString() : "";
  };

  const handleOnBrowseFile = async (e) => {
    try {
      setState((prev) => ({ ...prev, isLoading: true }));
      await uploadFileService.truncateAssetBulkData();
      const file = e?.target?.files[0];
      const bufferArray = await file.arrayBuffer();
      const workbook = XLSX.read(bufferArray, { type: "buffer" });
      if (workbook.SheetNames.length > 1) {
        await showErrorMessage(
          "The document contains multiple worksheets. Only one worksheet is allowed in the workbook."
        );
        setState((prev) => ({ ...prev, isLoading: false }));
        document.getElementById("browsefile").value = null;
        return;
      }
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: null });
      if (!jsonData || jsonData.length === 0) {
        await showWarningMessage(
          "The uploaded Excel file is empty. Please fill at least one row and try again."
        );
        setState((prev) => ({ ...prev, isLoading: false }));
        document.getElementById("browsefile").value = null;
        return;
      }

      //Column Validation
      //#region
      const requiredColumns = [
        "Name",
        "Group",
        "Category",
        "Sub Category",
        "Project",
        "Location",
        "VSE Asset Id",
        "SAP/ERP Asset Id",
        "SAP Equipment Id",
        "Custodian 1 Department",
        "Custodian 1 User",
        "Custodian 2 Department",
        "Custodian 2 User",
        "Custodian 3 Department",
        "Custodian 3 User",
        "Assign To",
        "Owner Type",
      ];
      let rowErrors = {};
      jsonData.forEach((row, index) => {
        const missingCols = [];
        requiredColumns.forEach((col) => {
          const value = row[col];
          if (
            value === null ||
            value === undefined ||
            value.toString().trim() === ""
          ) {
            missingCols.push(col);
          }
        });

        const assignTo = row["Assign To"]; //?.toString().trim().toLowerCase();
        if (assignTo === "Department" || assignTo === "User") {
          if (!row["Custodian 4 Department"]?.toString().trim()) {
            missingCols.push("Custodian 4 Department");
          }
          if (!row["Custodian 4 User"]?.toString().trim()) {
            missingCols.push("Custodian 4 User");
          }
        }
        if (assignTo === "Vendor") {
          if (!row["Custodian 4 Vendor"]?.toString().trim()) {
            missingCols.push("Custodian 4 Vendor");
          }
          if (!row["Custodian 4 Vendor Name"]?.toString().trim()) {
            missingCols.push("Custodian 4 Vendor Name");
          }
        }

        const ownerType = row["Owner Type"];
        if (ownerType === "Own") {
          if (!row["Vendor"]?.toString().trim()) {
            missingCols.push("Vendor");
          }
          if (!row["PO No"]?.toString().trim()) {
            missingCols.push("PO No");
          }
          if (!row["Purchase Date"]) {
            missingCols.push("Purchase Date");
          }
          if (!row["Purchase Value"]) {
            missingCols.push("Purchase Value");
          }
        }

        if (ownerType === "Hire") {
          if (!row["Vendor"]?.toString().trim()) {
            missingCols.push("Vendor");
          }
          if (!row["WO No"]?.toString().trim()) {
            missingCols.push("WO No");
          }
          if (!row["WO Date"]) {
            missingCols.push("WO Date");
          }
          if (!row["WO Duration"]) {
            missingCols.push("WO Duration");
          }
        }

        if (missingCols.length > 0) {
          rowErrors[index + 2] = missingCols;
        }
      });
      if (Object.keys(rowErrors).length > 0) {
        const errorLines = Object.entries(rowErrors).map(
          ([row, cols]) => `Row ${row} → Missing column(s): ${cols.join(", ")}`
        );
        await showErrorMessage(
          `Please fill the following column(s):\n\n• ${errorLines.join("\n• ")}`
        );
        setState((prev) => ({ ...prev, isLoading: false }));
        document.getElementById("browsefile").value = null;
        return;
      }
      //#endregion

      const cols = [
        "Company Code",
        "Name",
        "Group",
        "Category",
        "Sub Category",
        "Project",
        "Location",
        "Description",
        "Asset Label No",
        "Make",
        "Make Year",
        "Model",
        "Serial / Registration No",
        "Engine Serial No",
        "Chasis No",
        "VSE Asset Id",
        "SAP/ERP Asset Id",
        "SAP Equipment Id",
        "Size",
        "Custodian 1 Department",
        "Custodian 1 User",
        "Custodian 2 Department",
        "Custodian 2 User",
        "Custodian 3 Department",
        "Custodian 3 User",
        "Assign To",
        "Custodian 4 Department",
        "Custodian 4 User",
        "Custodian 4 Vendor",
        "Custodian 4 Vendor Name",
        "Owner Type",
        "Vendor",
        "PO No",
        "WO No",
        "Purchase Date",
        "WO Date",
        "Purchase Value",
        "WO Duration",
        "Asset Reached At Site",
      ];
      var colKeys = jsonData.length > 0 ? Object.keys(jsonData[0]) : [];
      const normalize = (str) =>
        str?.toString().trim().toLowerCase().replace(/\s+/g, " ");
      const normalizedExpected = cols.map(normalize);
      const normalizedActual = colKeys.map(normalize);
      const missingColumns = cols.filter(
        (col) => !normalizedActual.includes(normalize(col))
      );
      const extraColumns = colKeys.filter(
        (col) => !normalizedExpected.includes(normalize(col))
      );
      //let isValidFile = cols.every((col) => colKeys.includes(col));
      const isValidFile =
        missingColumns.length === 0 && extraColumns.length === 0;
      debugger;
      if (isValidFile) {
        let rowData = null;
        rowData = [
          ...new Set(
            jsonData?.map((item) => ({
              CompanyCode: item["Company Code"]?.toString() ?? "",
              AssetName: item["Name"]?.toString() ?? "",
              GroupName: item["Group"]?.toString() ?? "",
              CategoryName: item["Category"]?.toString() ?? "",
              SubCategoryName: item["Sub Category"]?.toString() ?? "",
              ProjectName: item["Project"]?.toString() ?? "",
              Location: item["Location"]?.toString() ?? "",
              Description: item["Description"]?.toString() ?? "",
              AssetLabelNo: item["Asset Label No"]?.toString() ?? "",
              Make: item["Make"]?.toString() ?? "",
              MakeYear: item["Make Year"]?.toString() ?? "",
              Model: item["Model"]?.toString() ?? "",
              SerialNo: item["Serial / Registration No"]?.toString() ?? "",
              EngineSerialNo: item["Engine Serial No"]?.toString() ?? "",
              ChasisNo: item["Chasis No"]?.toString() ?? "",
              VseAssetId: item["VSE Asset Id"]?.toString() ?? "",
              SapAssetId: item["SAP/ERP Asset Id"]?.toString() ?? "",
              SapEquipmentId: item["SAP Equipment Id"]?.toString() ?? "",

              Size: item["Size"]?.toString() ?? "",
              Custodian1DeptName:
                item["Custodian 1 Department"]?.toString() ?? "",
              Custodian1UserName: item["Custodian 1 User"]?.toString() ?? "",
              Custodian2DeptName:
                item["Custodian 2 Department"]?.toString() ?? "",
              Custodian2UserName: item["Custodian 2 User"]?.toString() ?? "",
              Custodian3DeptName:
                item["Custodian 3 Department"]?.toString() ?? "",
              Custodian3UserName: item["Custodian 3 User"]?.toString() ?? "",
              AssignTypeName: item["Assign To"]?.toString() ?? "",
              Custodian4DeptName:
                item["Custodian 4 Department"]?.toString() ?? "",
              Custodian4UserName: item["Custodian 4 User"]?.toString() ?? "",
              Custodian4VendorCompany:
                item["Custodian 4 Vendor"]?.toString() ?? "",
              Custodian4VendorName:
                item["Custodian 4 Vendor Name"]?.toString() ?? "",
              OwnerTypeName: item["Owner Type"]?.toString() ?? "",
              VendorName: item["Vendor"]?.toString() ?? "",
              PoNumber: item["PO No"]?.toString() ?? "",
              PurchaseDate: convertExcelDate(
                item["Purchase Date"],
                DefaultDateFormat.SqlDateFormat
              ),
              PurchasePrice: item["Purchase Value"] ?? 0,
              WoNumber: item["WO No"]?.toString() ?? "",
              WorkOrderDate: convertExcelDate(
                item["WO Date"],
                DefaultDateFormat.SqlDateFormat
              ),
              WoDuration: item["WO Duration"] ?? 0,
              AssetReachedAtSite: convertExcelDate(
                item["Asset Reached At Site"],
                DefaultDateFormat.SqlDateFormat
              ),
            }))
          ),
        ];
        setState((prev) => ({
          ...prev,
          importAssetDataLst: rowData,
          importAssetCount: rowData?.length,
        }));
        await handleOnSaveImportedAsset(rowData?.length, rowData, file?.name);
        setState((prev) => ({ ...prev, isLoading: false }));
      } else {
        let errorMsg = "";
        if (missingColumns.length > 0) {
          errorMsg += `Missing column(s):\n• ${missingColumns.join(
            "\n• "
          )}\n\n`;
        }
        if (extraColumns.length > 0) {
          errorMsg += `Unexpected / misspelled column(s):\n• ${extraColumns.join(
            "\n• "
          )}`;
        }
        await showErrorMessage(errorMsg.trim());
        setState((prev) => ({ ...prev, isLoading: false }));
        document.getElementById("browsefile").value = null;
      }
    } catch (error) {
      console.log(error);
      setState((prev) => ({ ...prev, isLoading: false }));
      document.getElementById("browsefile").value = null;
    }
  };

  const handleOnSaveImportedAsset = async (count, rowData, fileName) => {
    try {
      if (count === 0 || refIsImporting?.current) {
        return;
      }
      refIsImporting.current = true;
      let assetDataLst = [
        ...new Set(
          rowData?.map((item, index) => ({
            ...item,
            rowId: index + 1,
          }))
        ),
      ];
      let totalSycnced = 0;
      let batchSize = 10;
      let totalAsset = assetDataLst?.length ?? 0;
      if (totalAsset > 100) {
        batchSize = Math.ceil((totalAsset / 100) * 2);
      }
      let isSuccess = false;
      let importFailedCount = 0;
      for (let index = 0; index < totalAsset; index = index + batchSize) {
        if (index > 0 && !refIsImporting?.current) {
          break;
        }
        let newAssetDataLst = [
          ...new Set(assetDataLst?.slice(index, index + batchSize)),
        ];
        if (newAssetDataLst?.length === 0) {
          break;
        }
        try {
          let body = {
            userId: user?.userId,
            fileName: fileName,
            uploadAssetBulkDataLst: newAssetDataLst,
          };
          const response = await uploadFileService.uploadAsset(body);
          if (response?.isSuccess) {
            isSuccess = true;
          } else {
            importFailedCount += 1;
          }
        } catch (resError) {
          console.log(resError);
          importFailedCount += 1;
        }
        totalSycnced += newAssetDataLst?.length ?? 0;
        setState((prev) => ({
          ...prev,
          saveImportedAssetCount: totalSycnced,
        }));
        refDataGrid?.current?.instance?.repaint();
        refDataGrid?.current?.instance?.refresh();
        if (totalSycnced >= totalAsset) {
          break;
        }
      }
      refIsImporting.current = false;
      setState((prev) => ({
        ...prev,
        importAssetDataLst: [],
        saveImportedAssetCount: totalSycnced,
      }));
      if (totalSycnced > 0) {
        let message = "";
        if (isSuccess) {
          message = `Asset data(s) [ ${
            totalSycnced - importFailedCount
          } ] has been imported successfully`;
          await showResponseMessage({
            isSuccess: true,
            message: message,
          });
          await getAssetBulkLst();
        } else {
          await showErrorMessage("Failed to import asset data");
        }
        if (importFailedCount > 0 && totalAsset > 0) {
          await showErrorMessage("Some of asset data failed to import");
        }
      }
      document.getElementById("browsefile").value = null;
      handleOnResetImporting();
    } catch (error) {
      console.log(error);
      refIsImporting.current = false;
    }
  };

  const handleOnResetImporting = () => {
    try {
      setState((prev) => ({
        ...prev,
        importAssetDataLst: [],
        importAssetCount: 0,
        saveImportedAssetCount: 0,
      }));
      refIsImporting.current = false;
    } catch (error) {
      console.log(error);
    }
  };

  const handleOnProcessAsset = async (e) => {
    e.preventDefault();
    try {
      let pendingProcessAssetDetails = state.assetBulkDataSourceLst.filter(
        (f) => !f.isProcessed && f.isValid
      );
      if (
        pendingProcessAssetDetails?.length === 0 &&
        refIsProcessing?.current
      ) {
        return;
      }
      refIsProcessing.current = true;
      let pendingAssetLst = [
        ...new Set(
          pendingProcessAssetDetails?.map((item, index) => ({
            ...item,
            rowId: index + 1,
          }))
        ),
      ];
      let totalSycnced = 0;
      let batchSize = 10;
      let totalAsset = pendingAssetLst?.length ?? 0;
      if (totalAsset > 100) {
        batchSize = Math.ceil((totalAsset / 100) * 2);
      }
      let isSuccess = false;
      let processFailedCount = 0;
      for (let index = 0; index < totalAsset; index = index + batchSize) {
        if (index > 0 && !refIsProcessing?.current) {
          break;
        }
        let assetDetails = [
          ...new Set(pendingAssetLst?.slice(index, index + batchSize)),
        ];
        if (assetDetails?.length === 0) {
          break;
        }
        try {
          let body = {
            assetBulkData: assetDetails,
            userId: user?.userId,
          };
          const response = await uploadFileService.processAsset(body);
          if (response.isSuccess) {
            isSuccess = true;
          } else {
            processFailedCount += 1;
          }
        } catch (resError) {
          console.log(resError);
          processFailedCount += 1;
        }
        totalSycnced += assetDetails?.length ?? 0;
        setState((prev) => ({
          ...prev,
          processAssetCount: totalSycnced,
          assetBulkDataSourceLst: assetDetails,
        }));
        refDataGrid?.current?.instance?.repaint();
        refDataGrid?.current?.instance?.refresh();
        if (totalSycnced >= totalAsset) {
          break;
        }
      }
      refIsProcessing.current = false;
      setState((prev) => ({
        ...prev,
        processAssetCount: totalSycnced,
      }));
      if (totalSycnced > 0) {
        if (isSuccess) {
          await getAssetBulkLst();
          await showResponseMessage({
            isSuccess: true,
            message: "Asset processed successfully!!",
          });
        } else if (processFailedCount > 0 && totalAsset > 0) {
          setState((prev) => ({
            ...prev,
            processAssetCount: 0,
          }));
          await showErrorMessage("Some of asset failed to process.");
          return;
        } else {
          setState((prev) => ({
            ...prev,
            processAssetCount: 0,
          }));
          await showErrorMessage("Failed to process asset.");
          return;
        }
      }
      await handleOnResetProcessing();
    } catch (error) {
      console.log(error);
      refIsProcessing.current = false;
    }
  };

  const handleOnResetProcessing = async () => {
    try {
      setState((prev) => ({
        ...prev,
        processAssetCount: 0,
      }));
      refIsProcessing.current = false;
    } catch (error) {
      console.log(error);
    }
  };

  const importingAssetProgressStatusFormat = useCallback(
    (value, ratio) => {
      try {
        let statusTitle = value * 100 === 100 ? "Completed" : "Syncing";
        return `${statusTitle} - ${
          (value * 100).toFixed(0) + "% (" + ratio + ")"
        }`;
      } catch (error) {
        console.log(error);
      }
    },
    [state.importAssetCount]
  );

  const processingAssetProgressStatusFormat = useCallback(
    (value, ratio) => {
      try {
        let statusTitle = value * 100 === 100 ? "Completed" : "Syncing";
        return `${statusTitle} - ${
          (value * 100).toFixed(0) + "% (" + ratio + ")"
        }`;
      } catch (error) {
        console.log(error);
      }
    },
    [state.processAssetCount]
  );

  const buildGridData = (parsedResult) => {
    const rows = [];
    parsedResult?.alreadyExists.forEach((item, index) => {
      rows.push({
        id: `AE-${index}`,
        type: "Already Exists In Asset",
        description: item,
      });
    });
    debugger;
    parsedResult?.doesNotExist.forEach((item, index) => {
      rows.push({
        id: `DNE-${index}`,
        type: "Does Not Exist",
        description: item,
      });
    });
    return rows;
  };

  const parseAssetMessage = (message) => {
    const result = {
      alreadyExists: [],
      doesNotExist: [],
    };
    if (!message || typeof message !== "string") {
      return result;
    }
    // const parts = message?.split(",");
    const parts = message.split(/,| and /i);
    parts.forEach((part) => {
      const text = part?.trim();
      if (text?.includes("already exists in asset")) {
        result?.alreadyExists.push(
          text?.replace("already exists in asset", "").trim()
        );
      } else if (text?.includes("does not exists")) {
        result?.doesNotExist.push(text.replace("does not exists", "").trim());
      } else {
        result?.alreadyExists.push(text);
      }
    });
    setRemarkArray(buildGridData(result) ?? []);
  };

  return (
    <>
      <div className="content">
        <div className="card" style={{ height: "70px" }}>
          <div className="card-body">
            <div className="row">
              <div
                className="col-12 col-lg-6"
                // className={`col-12 col-lg-${
                //   (state.assetBulkDataSourceLst?.filter(
                //     (f) => f.isProcessed && f.isValid
                //   )?.length &&
                //     state.assetBulkDataSourceLst?.length) ||
                //   state.assetBulkDataSourceLst?.length === 0
                //     ? `4`
                //     : `6`
                // }`}
              >
                <div className="form-group mb-2">
                  <button
                    type="button"
                    title="Back"
                    className="btn btn-outline-primary btn-sm"
                    onClick={() => {
                      try {
                        navigate("/Asset");
                      } catch (error) {
                        console.log(error);
                      }
                    }}
                  >
                    <span title="Back ">
                      <i className="fas fa-arrow-left" />
                      &nbsp;&nbsp;Back
                    </span>
                  </button>
                  &nbsp;&nbsp;&nbsp;
                  <button
                    type="button"
                    title="Download Sample File"
                    className="btn btn-outline-secondary btn-sm"
                    onClick={(e) => {
                      try {
                        e.preventDefault();
                        window.location.href =
                          "./assets/excel/SampleAsset.xlsx";
                      } catch (error) {
                        console.log(error);
                      }
                    }}
                  >
                    <span title="Download Sample File">
                      <i className="fas fa-file-download"></i>
                      &nbsp;&nbsp;Download Sample File
                    </span>
                  </button>
                  &nbsp;&nbsp;&nbsp;
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm"
                    title="Browse File"
                    onClick={() => {
                      document.getElementById("browsefile").click();
                    }}
                    disabled={state.isLoading}
                  >
                    <span title="Browse File">
                      <i
                        className={`fas fa-${
                          state.isLoading ? "spinner fa-spin" : "file-import"
                        }`}
                      />
                      &nbsp;&nbsp;Browse File
                    </span>
                  </button>
                  <input
                    id="browsefile"
                    type="file"
                    className="input"
                    onChange={async (e) => {
                      await handleOnBrowseFile(e);
                    }}
                    hidden={true}
                  />
                  {state.importAssetCount === 0 &&
                    state.processAssetCount === 0 && (
                      <>
                        &nbsp;&nbsp;&nbsp;
                        <button
                          type="button"
                          title="Process"
                          className="btn btn-outline-primary btn-sm"
                          onClick={async (e) => {
                            await handleOnProcessAsset(e);
                          }}
                          hidden={
                            (state.assetBulkDataSourceLst?.filter(
                              (f) => f.isProcessed && f.isValid
                            )?.length &&
                              state.assetBulkDataSourceLst?.length) ||
                            state.assetBulkDataSourceLst?.length === 0
                          }
                          disabled={
                            state.processAssetCount > 0 ||
                            refIsProcessing?.current
                          }
                        >
                          <i
                            className={`${
                              refIsProcessing?.current
                                ? "fas fa-spinner fa-spin"
                                : "fas fa-filter"
                            }`}
                          />
                          &nbsp;Process
                        </button>
                      </>
                    )}
                </div>
              </div>
              <div className="col-12 col-lg-4">
                {state.importAssetCount > 0 && (
                  <>
                    <span style={{ fontSize: "Medium" }}>
                      {`Category Left : (${
                        state.importAssetCount -
                        Number(state.saveImportedAssetCount)
                      }/${state.importAssetCount})`}
                    </span>
                    <ProgressBar
                      width="100%"
                      min={0}
                      max={state.importAssetCount}
                      value={Number(state.saveImportedAssetCount)}
                      statusFormat={importingAssetProgressStatusFormat}
                    />
                  </>
                )}
                {state.processAssetCount > 0 && (
                  <>
                    <span style={{ fontSize: "Medium" }}>
                      {`Processing Category: (${
                        state.assetBulkDataSourceLst?.length -
                        Number(state.processAssetCount)
                      }/${state.assetBulkDataSourceLst?.length})`}
                    </span>
                    <ProgressBar
                      width="100%"
                      min={0}
                      max={state.assetBulkDataSourceLst?.length}
                      value={Number(state.processAssetCount)}
                      statusFormat={processingAssetProgressStatusFormat}
                    />
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <DataGrid
              ref={refDataGrid}
              keyExpr="id"
              dataSource={state.assetBulkDataSourceLst}
              showColumnLines={false}
              showRowLines={true}
              showBorders={true}
              allowColumnResizing={false}
              allowColumnReordering={true}
              columnAutoWidth={true}
              onCellPrepared={(e) => {
                if (e.rowType === "data") {
                  if (e?.data?.isValid) {
                    e.cellElement.style.backgroundColor = "#a6daabff";
                  } else {
                    e.cellElement.style.backgroundColor = "#ee8282ff";
                  }
                }
              }}
              onToolbarPreparing={(e) => {
                const items = [];
                items.push({
                  location: "after",
                  widget: "dxButton",
                  options: {
                    icon: "refresh",
                    text: "Refresh",
                    hint: "Refresh",
                    type: "default",
                    stylingMode: "outlined",
                    onClick: async () => {
                      getAssetBulkLst();
                    },
                  },
                });
                e.toolbarOptions.items.unshift(...items);
              }}
            >
              <Column
                dataField="remark"
                caption="Remark"
                allowSearch={true}
                allowFiltering={false}
                allowSorting={true}
                width={450}
                cellRender={(data) => {
                  if (!data?.data.isValid) {
                    return (
                      <>
                        <div
                          title="Click to view details"
                          style={{
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            cursor: "pointer",
                            color: "#0d6efd",
                          }}
                          onClick={() => {
                            setIsOpenPopup(true);
                            parseAssetMessage(data?.value);
                          }}
                        >
                          {data?.value}
                        </div>
                      </>
                    );
                  } else {
                    return (
                      <>
                        <span>{data?.value}</span>
                      </>
                    );
                  }
                }}
              />
              <Column
                dataField="assetName"
                caption="Name"
                allowFiltering={false}
                allowSearch={true}
                allowSorting={true}
                calculateCellValue={(data) => {
                  return `${data?.assetName || ""} ${data?.description || ""}`;
                }}
                cellRender={(data) => {
                  return (
                    <>
                      <span>{data?.data?.assetName ?? ""}</span>
                      {data?.data?.description && (
                        <>
                          <br />
                          <div style={{ color: "#b1b1b1", fontSize: "11px" }}>
                            {data?.data?.description ?? ""}
                          </div>
                        </>
                      )}
                    </>
                  );
                }}
              />
              <Column
                dataField="groupName"
                caption="Group"
                allowFiltering={false}
                allowSearch={true}
                allowSorting={true}
              />
              <Column
                dataField="categoryName"
                caption="Category"
                allowFiltering={false}
                allowSearch={true}
                allowSorting={true}
              />
              <Column
                dataField="subCategoryName"
                caption="Sub Category"
                allowFiltering={false}
                allowSearch={true}
                allowSorting={true}
              />
              <Column
                dataField="projectName"
                caption="Asset Site / Location"
                allowFiltering={false}
                allowSearch={true}
                allowSorting={true}
                calculateCellValue={(data) => {
                  return `${data?.projectName || ""} ${data?.location || ""}`;
                }}
                cellRender={(data) => {
                  return (
                    <>
                      <span>{data?.data?.projectName ?? ""}</span>
                      {data?.data?.location && (
                        <>
                          <br />
                          <div style={{ color: "#b1b1b1", fontSize: "11px" }}>
                            {data?.data?.location ?? ""}
                          </div>
                        </>
                      )}
                    </>
                  );
                }}
              />
              <Column
                dataField="vseAssetId"
                caption="VSE Asset Id"
                allowFiltering={false}
                allowSearch={true}
                allowSorting={true}
              />
              <Column
                dataField="sapAssetId"
                caption="SAP/ERP Asset Id"
                allowFiltering={false}
                allowSearch={true}
                allowSorting={true}
              />

              <Column
                dataField="custodian1UserName"
                caption="Custodian 1"
                allowFiltering={false}
                allowSearch={true}
                allowSorting={true}
                calculateCellValue={(data) => {
                  return `${data?.custodian1UserName || ""} ${
                    data?.custodian1DeptName || ""
                  }`;
                }}
                cellRender={(data) => {
                  if (data?.data?.custodian1UserName) {
                    return (
                      <>
                        <span>{data?.data?.custodian1UserName ?? ""}</span>
                        {data?.data?.custodian1DeptName && (
                          <>
                            <br />
                            <div style={{ color: "#b1b1b1", fontSize: "11px" }}>
                              {data?.data?.custodian1DeptName ?? ""}
                            </div>
                          </>
                        )}
                      </>
                    );
                  } else {
                    return "";
                  }
                }}
              />
              <Column
                dataField="custodian2UserName"
                caption="Custodian 2"
                allowFiltering={false}
                allowSearch={true}
                allowSorting={true}
                calculateCellValue={(data) => {
                  return `${data?.custodian2UserName || ""} ${
                    data?.custodian2DeptName || ""
                  }`;
                }}
                cellRender={(data) => {
                  if (data?.data?.custodian2UserName) {
                    return (
                      <>
                        <span>{data?.data?.custodian2UserName ?? ""}</span>
                        {data?.data?.custodian2DeptName && (
                          <>
                            <br />
                            <div style={{ color: "#b1b1b1", fontSize: "11px" }}>
                              {data?.data?.custodian2DeptName ?? ""}
                            </div>
                          </>
                        )}
                      </>
                    );
                  } else {
                    return "";
                  }
                }}
              />
              <Column
                dataField="custodian3UserName"
                caption="Custodian 3"
                allowFiltering={false}
                allowSearch={true}
                allowSorting={true}
                calculateCellValue={(data) => {
                  return `${data?.custodian3UserName || ""} ${
                    data?.custodian3DeptName || ""
                  }`;
                }}
                cellRender={(data) => {
                  if (data?.data?.custodian3UserName) {
                    return (
                      <>
                        <span>{data?.data?.custodian3UserName ?? ""}</span>
                        {data?.data?.custodian3DeptName && (
                          <>
                            <br />
                            <div style={{ color: "#b1b1b1", fontSize: "11px" }}>
                              {data?.data?.custodian3DeptName ?? ""}
                            </div>
                          </>
                        )}
                      </>
                    );
                  } else {
                    return "";
                  }
                }}
              />
              <Column
                dataField="custodian4DeptName"
                caption="Assign To"
                allowFiltering={false}
                allowSearch={true}
                allowSorting={true}
                calculateCellValue={(data) => {
                  return `${data?.custodian4UserName || ""} ${
                    data?.custodian4DeptName || ""
                  }`;
                }}
                cellRender={(data) => {
                  if (data?.data?.custodian4DeptName) {
                    return (
                      <>
                        <span>{data?.data?.custodian4DeptName ?? ""}</span>
                        {data?.data?.custodian4UserName && (
                          <>
                            <br />
                            <div style={{ color: "#b1b1b1", fontSize: "11px" }}>
                              {data?.data?.custodian4UserName ?? ""}
                            </div>
                          </>
                        )}
                      </>
                    );
                  } else {
                    return "";
                  }
                }}
              />
              <Column
                dataField="createdDate"
                caption="Created On"
                allowSearch={true}
                allowFiltering={false}
                calculateCellValue={(data) => {
                  return `${data?.createdDate || ""} ${
                    data?.createdByUserName || ""
                  }`;
                }}
                cellRender={(data) => {
                  if (data?.data?.createdDate) {
                    return (
                      <>
                        <span>
                          {moment(data?.data?.createdDate).format(
                            "DD/MM/YYYY"
                          ) +
                            " " +
                            moment(data?.data?.createdDate).format("LT") ?? ""}
                        </span>
                        <br />
                        <div style={{ color: "#000000ff", fontSize: "11px" }}>
                          {data?.data?.createdByUserName ?? ""}
                        </div>
                      </>
                    );
                  } else {
                    return "";
                  }
                }}
              />
              <HeaderFilter visible={false} />
              <SearchPanel
                visible={true}
                highlightCaseSensitive={true}
                width={240}
                placeholder="Search..."
              />
              <Paging defaultPageSize={10} />
              <Pager
                allowedPageSizes={[10, 15, 20, 50, 100]}
                showPageSizeSelector={true}
                showInfo={true}
                visible={true}
                showNavigationButtons={true}
              />
              <Export enabled={true} fileName="Asset Bulk Data" />
            </DataGrid>
          </div>
        </div>
      </div>
      <Popup
        visible={isOpenPopup}
        onHiding={() => {
          setIsOpenPopup(false);
        }}
        title="Remark Details"
        width={1000}
        height={"auto"}
      >
        <div style={{ whiteSpace: "pre-wrap" }}>
          <DataGrid
            dataSource={remarkArray}
            showColumnLines={false}
            showRowLines={true}
            showBorders={true}
            allowColumnResizing={false}
            allowColumnReordering={true}
            columnAutoWidth={true}
          >
            <Column
              dataField="description"
              caption="Fields"
              allowFiltering={false}
              allowSearch={true}
              allowSorting={true}
            />
            <Column
              dataField="type"
              caption="Description"
              allowFiltering={false}
              allowSearch={true}
              allowSorting={true}
            />
          </DataGrid>
        </div>
      </Popup>
      {state.isCompleteLoading && (
        <Loader loadingMessage={state.loadingMessage} />
      )}
    </>
  );
}
